import { baseApi } from "../baseApi";
import { pick } from "../util";

export const EMAIL_TEMPLATE_KEYS = [
  "password-sent",
  "user-created",
  "product-created",
  "product-updated",
  "product-deleted",
] as const;

export type EmailTemplateKey = (typeof EMAIL_TEMPLATE_KEYS)[number];

export const ORG_NOTIFY_ROLES = ["owner", "admin", "manager", "employee"] as const;

export interface EmailTemplate {
  id: string;
  organizationId: string;
  templateKey: string;
  subject: string;
  body: string;
  enabled: boolean;
  notifyRoles: string[];
  createdBy?: string | null;
  updatedBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateEmailTemplateContentInput {
  subject?: string;
  body?: string;
  enabled?: boolean;
}

export interface UpdateEmailTemplateRecipientsInput {
  notifyRoles: string[];
}

const EMAIL_TEMPLATE_FIELDS = `
  id
  organizationId
  templateKey
  subject
  body
  enabled
  notifyRoles
  createdBy
  updatedBy
  createdAt
  updatedAt
`;

export const emailTemplateApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getEmailTemplates: builder.query<EmailTemplate[], string>({
      query: (organizationId) => ({
        body: {
          query: `
            query EmailTemplates($organizationId: String!) {
              emailTemplates(organizationId: $organizationId) {
                ${EMAIL_TEMPLATE_FIELDS}
              }
            }
          `,
          variables: { organizationId },
        },
      }),

      transformResponse: (res: unknown) =>
        pick<EmailTemplate[]>(res, "emailTemplates"),

      providesTags: (result, _err, organizationId) =>
        result
          ? [
              ...result.map((t) => ({
                type: "EmailTemplate" as const,
                id: t.id || `${organizationId}:${t.templateKey}`,
              })),
              { type: "EmailTemplate" as const, id: `ORG-${organizationId}` },
            ]
          : [{ type: "EmailTemplate" as const, id: `ORG-${organizationId}` }],
    }),

    updateEmailTemplateContent: builder.mutation<
      EmailTemplate,
      { organizationId: string; templateKey: string; input: UpdateEmailTemplateContentInput }
    >({
      query: ({ organizationId, templateKey, input }) => ({
        body: {
          query: `
            mutation UpdateEmailTemplateContent(
              $organizationId: String!
              $templateKey: String!
              $input: UpdateEmailTemplateContentInput!
            ) {
              updateEmailTemplateContent(
                organizationId: $organizationId
                templateKey: $templateKey
                input: $input
              ) {
                ${EMAIL_TEMPLATE_FIELDS}
              }
            }
          `,
          variables: { organizationId, templateKey, input },
        },
      }),

      transformResponse: (res: unknown) =>
        pick<EmailTemplate>(res, "updateEmailTemplateContent"),

      invalidatesTags: (_result, _err, { organizationId }) => [
        { type: "EmailTemplate", id: `ORG-${organizationId}` },
      ],
    }),

    updateEmailTemplateRecipients: builder.mutation<
      EmailTemplate,
      { organizationId: string; templateKey: string; input: UpdateEmailTemplateRecipientsInput }
    >({
      query: ({ organizationId, templateKey, input }) => ({
        body: {
          query: `
            mutation UpdateEmailTemplateRecipients(
              $organizationId: String!
              $templateKey: String!
              $input: UpdateEmailTemplateRecipientsInput!
            ) {
              updateEmailTemplateRecipients(
                organizationId: $organizationId
                templateKey: $templateKey
                input: $input
              ) {
                ${EMAIL_TEMPLATE_FIELDS}
              }
            }
          `,
          variables: { organizationId, templateKey, input },
        },
      }),

      transformResponse: (res: unknown) =>
        pick<EmailTemplate>(res, "updateEmailTemplateRecipients"),

      invalidatesTags: (_result, _err, { organizationId }) => [
        { type: "EmailTemplate", id: `ORG-${organizationId}` },
      ],
    }),
  }),
});

export const {
  useGetEmailTemplatesQuery,
  useLazyGetEmailTemplatesQuery,
  useUpdateEmailTemplateContentMutation,
  useUpdateEmailTemplateRecipientsMutation,
} = emailTemplateApi;
